import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { LocalApiCheckService } from './local-api-check.service';
import { EnvironmentService } from './environment.service';

export interface ApiEnvironment {
  name: string;
  displayName: string;
  apiUrl: string;
  wsUrl: string;
  stripeKey: string;
  isProduction: boolean;
  requiresAvailabilityCheck?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ApiEnvironmentService {
  private readonly STORAGE_KEY = 'selectedApiEnvironment';
  private localApiCheckService = inject(LocalApiCheckService);
  private environmentService = inject(EnvironmentService);

  // Available environments
  private readonly environments: ApiEnvironment[] = [
    {
      name: 'local',
      displayName: 'Local',
      apiUrl: 'http://localhost:8080',
      wsUrl: 'http://localhost:8080/ws',
      stripeKey: 'pk_test_51OjOE7DGEe6UUXl1GNTuvkOylTvqdIRCCBsnkSM96F0oTlUZl3RUHy5sFFmWTGE6C5yCnZxrJ99KIzUVqgVFQnqA00m2hJ00UQ',
      isProduction: false,
      requiresAvailabilityCheck: true
    },
    {
      name: 'development',
      displayName: 'Development',
      apiUrl: 'https://api.dev.sunnystreet.com',
      wsUrl: 'https://api.dev.sunnystreet.com/ws',
      stripeKey: 'pk_test_51OjOE7DGEe6UUXl1GNTuvkOylTvqdIRCCBsnkSM96F0oTlUZl3RUHy5sFFmWTGE6C5yCnZxrJ99KIzUVqgVFQnqA00m2hJ00UQ',
      isProduction: false
    },
    {
      name: 'production',
      displayName: 'Production',
      apiUrl: 'https://api.sunnystreet.com',
      wsUrl: 'https://api.sunnystreet.com/ws',
      stripeKey: 'pk_live_51OjOE7DGEe6UUXl1QwAxU7Wbe25SOaHiCdkJ1ry73QPQ6yCCrboXASfalu1g3NjtyvmU1HvD0OtIeo7JmM45QsSm00w4XyVmwF',
      isProduction: true
    }
  ];

  private currentEnvironmentSubject: BehaviorSubject<ApiEnvironment>;
  public currentEnvironment$: Observable<ApiEnvironment>;

  constructor() {
    // In production builds, always use production environment regardless of saved preferences
    // In development builds, allow saved preferences or default to development
    let initialEnv: ApiEnvironment;
    
    if (this.environmentService.isProductionBuild()) {
      // Force production environment when built for production
      initialEnv = this.environments.find(env => env.name === 'production') || this.getDefaultEnvironment();
      // Clear any saved preference that might override production
      this.clearEnvironmentPreference();
    } else {
      // In development, allow switching between environments
      const savedEnv: ApiEnvironment | null = this.getSavedEnvironment();
      initialEnv = savedEnv || this.getDefaultEnvironment();
    }
    
    this.currentEnvironmentSubject = new BehaviorSubject<ApiEnvironment>(initialEnv);
    this.currentEnvironment$ = this.currentEnvironmentSubject.asObservable();
  }

  /**
   * Get the current API environment
   */
  getCurrentEnvironment(): ApiEnvironment {
    return this.currentEnvironmentSubject.value;
  }

  /**
   * Get the current API URL
   */
  getCurrentApiUrl(): string {
    return this.currentEnvironmentSubject.value.apiUrl;
  }

  /**
   * Get the current WebSocket URL
   */
  getCurrentWsUrl(): string {
    return this.currentEnvironmentSubject.value.wsUrl;
  }

  /**
   * Get all available environments
   */
  getAvailableEnvironments(): ApiEnvironment[] {
    return [...this.environments];
  }
  
  getAvailableEnvironmentsWithCheck(): Observable<ApiEnvironment[]> {
    const showSelector: boolean = this.environmentService.showEnvironmentSelector();
    if (!showSelector) {
      return of(this.environments.filter(env => env.name !== 'local'));
    }

    // In dev mode, always show all environments including local
    return of([...this.environments]);
  }

  switchEnvironment(environmentName: string): void {
    // Prevent switching environments in production builds
    const canSwitch: boolean = this.environmentService.showEnvironmentSelector();
    if (!canSwitch) {
      console.warn('Cannot switch environments in production build');
      return;
    }
    
    const newEnv: ApiEnvironment | undefined = this.environments.find(env => env.name === environmentName);
    
    if (newEnv) {
      this.currentEnvironmentSubject.next(newEnv);
      this.saveEnvironmentPreference(environmentName);
      
    } else {
      console.error(`Environment ${environmentName} not found`);
    }
  }

  /**
   * Check if currently using production API
   */
  isProductionApi(): boolean {
    return this.currentEnvironmentSubject.value.isProduction;
  }

  private getDefaultEnvironment(): ApiEnvironment {
    // Use the build configuration to determine the default environment
    // In dev mode, default to development; in production, default to production
    const defaultEnvName: "development" | "production" = this.environmentService.isProductionBuild() ? 'production' : 'development';
    return this.environments.find(env => env.name === defaultEnvName) || this.environments[0];
  }

  private saveEnvironmentPreference(environmentName: string): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, environmentName);
    } catch (error) {
      console.error('Failed to save environment preference:', error);
    }
  }

  private getSavedEnvironment(): ApiEnvironment | null {
    try {
      const savedName: string | null = localStorage.getItem(this.STORAGE_KEY);
      if (savedName) {
        return this.environments.find(env => env.name === savedName) || null;
      }
    } catch (error) {
      console.error('Failed to load environment preference:', error);
    }
    return null;
  }

  clearEnvironmentPreference(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear environment preference:', error);
    }
  }
}
